// src/pages/dashboard/MapPage.tsx
//
// Mirrors mobile MapScreen.tsx exactly:
//  • Same three modes: properties | businesses | friends
//  • Same API endpoints: /map/properties, /map/businesses, /map/friends/locations
//  • Same default origin: Nairobi (-1.286389, 36.817223)
//  • Same PNG marker assets (copied from MAJENGO-FRONT/src/assets/markers/)
//  • Same MAP_CONFIG constants (5-min cache, 50 max markers, 0.05 delta)
//  • User location marker + locate-me button
//  • Geolocation via browser navigator (web equivalent of expo-location)
//
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Home, Store, Users, Locate, Loader2, RefreshCw,
  MapPin, Building2, Star, Phone, ExternalLink,
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { formatDistanceToNow } from 'date-fns';

// ── MAP_CONFIG — mirrors mobile mapConfig.ts exactly ─────────────────────────
const MAP_CONFIG = {
  CACHE_DURATION:       5 * 60 * 1000, // 5 minutes
  MAX_MARKERS:          50,
  DEFAULT_ZOOM: {
    latitudeDelta:  0.05,
    longitudeDelta: 0.05,
  },
  BATCH_SIZE:           100,
  MIN_REQUEST_INTERVAL: 1000,
};

// Default origin — Nairobi (same as mobile)
const DEFAULT_ORIGIN: [number, number] = [-1.286389, 36.817223];
const DEFAULT_ZOOM = 13;

// ── Marker icons — same PNG assets as mobile MARKER_ASSETS ───────────────────
// Mobile uses: property.png, business.png, friend.png, user_location.png
// We serve them from /public/markers/ (copied from MAJENGO-FRONT/src/assets/markers/)

const makeIcon = (src: string, size: [number, number] = [44, 44]) =>
  L.icon({
    iconUrl:    src,
    iconSize:   size,
    iconAnchor: [size[0] / 2, size[1]],   // anchor at bottom-center (same as mobile anchor:{x:0.5,y:1})
    popupAnchor:[0, -size[1]],
  });

const ICONS = {
  property: makeIcon('/markers/property.png'),
  business: makeIcon('/markers/business.png'),
  friend:   makeIcon('/markers/friend.png',   [48, 48]),
  user:     makeIcon('/markers/user_location.png', [40, 40]),
};

// Vacancy badge — green dot overlay (mirrors mobile vacancyBadge)
const makeVacancyIcon = () =>
  L.divIcon({
    html: `
      <div style="position:relative;width:44px;height:44px">
        <img src="/markers/property.png" style="width:44px;height:44px;object-fit:contain"/>
        <div style="position:absolute;top:-4px;right:-4px;background:#fff;border-radius:8px;padding:2px;
                    display:flex;align-items:center;justify-content:center;
                    box-shadow:0 1px 3px rgba(0,0,0,0.3)">
          <span style="color:#4CAF50;font-weight:700;font-size:11px;line-height:1">!</span>
        </div>
      </div>`,
    iconSize:   [44, 44],
    iconAnchor: [22, 44],
    popupAnchor:[0, -44],
    className:  '',
  });

// ── Types ─────────────────────────────────────────────────────────────────────

type MapMode = 'properties' | 'businesses' | 'friends';

interface Property {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  category?: string;
  has_vacancies?: boolean;
  price?: number;
}

interface Business {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  category?: string;
  primary_contact_phone?: string;
}

interface Friend {
  id: string;
  username: string;
  full_name?: string;
  avatar_url?: string;
  latitude: number;
  longitude: number;
  has_story?: boolean;
  mood?: string;
  last_seen?: string;
}

// ── FlyTo helper — animates map to a coordinate ───────────────────────────────
const FlyTo: React.FC<{ target: [number, number] | null }> = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(target, DEFAULT_ZOOM, { duration: 0.8 });
  }, [target]);
  return null;
};

// ── BoundsWatcher — fires on moveend/zoomend (for future nearby-items) ────────
const BoundsWatcher: React.FC<{ onMove: () => void }> = ({ onMove }) => {
  useMapEvents({ moveend: onMove, zoomend: onMove });
  return null;
};

// ── Popup cards ───────────────────────────────────────────────────────────────

const PropertyPopup: React.FC<{ p: Property }> = ({ p }) => (
  <div className="min-w-[180px] p-1">
    <div className="flex items-center gap-2 mb-1">
      <Home size={14} className="text-primary flex-shrink-0" />
      <span className="font-bold text-sm text-light-text dark:text-dark-text line-clamp-1">{p.name}</span>
    </div>
    {p.category && <p className="text-xs text-light-subtle dark:text-dark-subtle mb-1">{p.category}</p>}
    {p.has_vacancies && (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full mb-1">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Has vacancies
      </span>
    )}
    {p.price && (
      <p className="text-sm font-bold text-primary">KES {p.price.toLocaleString()}<span className="text-xs font-normal text-light-subtle">/mo</span></p>
    )}
  </div>
);

const BusinessPopup: React.FC<{ b: Business }> = ({ b }) => (
  <div className="min-w-[180px] p-1">
    <div className="flex items-center gap-2 mb-1">
      <Store size={14} className="text-green-600 flex-shrink-0" />
      <span className="font-bold text-sm text-light-text dark:text-dark-text line-clamp-1">{b.name}</span>
    </div>
    {b.category && <p className="text-xs text-light-subtle dark:text-dark-subtle mb-1">{b.category}</p>}
    {b.primary_contact_phone && (
      <div className="flex items-center gap-1 text-xs text-light-subtle dark:text-dark-subtle">
        <Phone size={11} /> {b.primary_contact_phone}
      </div>
    )}
  </div>
);

const FriendPopup: React.FC<{ f: Friend }> = ({ f }) => (
  <div className="min-w-[180px] p-1">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-[#0EA5E9] flex items-center justify-center flex-shrink-0 overflow-hidden">
        {f.avatar_url
          ? <img src={f.avatar_url} alt={f.username} className="w-full h-full object-cover" />
          : <span className="text-white font-bold text-sm">{f.username?.[0]?.toUpperCase()}</span>
        }
      </div>
      <div>
        <p className="font-bold text-sm text-light-text dark:text-dark-text">{f.full_name || f.username}</p>
        <p className="text-xs text-light-subtle dark:text-dark-subtle">@{f.username}</p>
      </div>
    </div>
    {f.has_story && (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full mb-1">
        <Star size={10} /> Has new story
      </span>
    )}
    {f.mood && <p className="text-xs text-light-subtle dark:text-dark-subtle">Mood: {f.mood}</p>}
    {f.last_seen && (
      <p className="text-[11px] text-light-subtle dark:text-dark-subtle mt-1">
        Last seen {formatDistanceToNow(new Date(f.last_seen), { addSuffix: true })}
      </p>
    )}
  </div>
);

// ── Main MapPage ──────────────────────────────────────────────────────────────

export const MapPage: React.FC = () => {
  const [mapMode, setMapMode]           = useState<MapMode>('properties');
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [flyTarget, setFlyTarget]       = useState<[number, number] | null>(null);
  const [properties, setProperties]     = useState<Property[]>([]);
  const [businesses, setBusinesses]     = useState<Business[]>([]);
  const [friends, setFriends]           = useState<Friend[]>([]);
  const [loading, setLoading]           = useState(false);
  const [geoLoading, setGeoLoading]     = useState(false);
  const cacheRef = useRef<Record<string, { data: any[]; ts: number }>>({});

  // ── Geolocation — web equivalent of expo-location getCurrentLocation ──────
  const getLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        const loc: [number, number] = [pos.coords.latitude, pos.coords.longitude];
        setUserLocation(loc);
        setFlyTarget(loc);
        setGeoLoading(false);
      },
      () => {
        // Fallback to Nairobi (same as mobile)
        setUserLocation(DEFAULT_ORIGIN);
        setGeoLoading(false);
      },
      { timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  useEffect(() => { getLocation(); }, []);

  // ── Fetch data — mirrors mobile fetchData() with 5-min cache ─────────────
  const fetchData = useCallback(async (mode: MapMode) => {
    const cached = cacheRef.current[mode];
    if (cached && Date.now() - cached.ts < MAP_CONFIG.CACHE_DURATION) {
      if (mode === 'properties') setProperties(cached.data);
      if (mode === 'businesses') setBusinesses(cached.data);
      if (mode === 'friends')    setFriends(cached.data);
      return;
    }

    setLoading(true);
    try {
      if (mode === 'properties') {
        const res = await apiClient.get('/map/properties');
        const data = (res.data || [])
          .filter((p: any) => p.latitude && p.longitude)
          .slice(0, MAP_CONFIG.MAX_MARKERS);
        setProperties(data);
        cacheRef.current.properties = { data, ts: Date.now() };
      } else if (mode === 'businesses') {
        const res = await apiClient.get('/map/businesses');
        const data = (res.data || [])
          .filter((b: any) => b.latitude && b.longitude)
          .slice(0, MAP_CONFIG.MAX_MARKERS);
        setBusinesses(data);
        cacheRef.current.businesses = { data, ts: Date.now() };
      } else {
        const res = await apiClient.get('/map/friends/locations');
        const data = (res.data || [])
          .filter((f: any) => f.latitude && f.longitude)
          .slice(0, MAP_CONFIG.MAX_MARKERS);
        setFriends(data);
        cacheRef.current.friends = { data, ts: Date.now() };
      }
    } catch (err) {
      console.error('MapPage fetch error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(mapMode); }, [mapMode]);

  // ── Mode toggle config — mirrors mobile modeRow ───────────────────────────
  const modes: { key: MapMode; label: string; Icon: React.FC<any>; color: string }[] = [
    { key: 'properties', label: 'Properties', Icon: Home,  color: '#4F46E5' },
    { key: 'businesses', label: 'Business',   Icon: Store, color: '#10B981' },
    { key: 'friends',    label: 'Friends',    Icon: Users, color: '#8e44ad' },
  ];

  // ── Count badge ───────────────────────────────────────────────────────────
  const count = mapMode === 'properties' ? properties.length
              : mapMode === 'businesses' ? businesses.length
              : friends.length;

  return (
    <div className="relative h-[calc(100vh-56px)] w-full bg-light-bg dark:bg-dark-bg overflow-hidden">

      {/* ── Map ── */}
      <MapContainer
        center={userLocation || DEFAULT_ORIGIN}
        zoom={DEFAULT_ZOOM}
        style={{ height: '100%', width: '100%', zIndex: 1 }}
        zoomControl={false}
      >
        {/* OpenStreetMap tiles — same provider as existing web app */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <FlyTo target={flyTarget} />
        <BoundsWatcher onMove={() => {}} />

        {/* User location marker — mirrors mobile <Marker coordinate={userLocation}> */}
        {userLocation && (
          <Marker position={userLocation} icon={ICONS.user}>
            <Popup>
              <div className="p-1 text-sm font-semibold text-primary">📍 You are here</div>
            </Popup>
          </Marker>
        )}

        {/* Property markers */}
        {mapMode === 'properties' && properties.map(p => (
          <Marker
            key={`property-${p.id}`}
            position={[p.latitude, p.longitude]}
            icon={p.has_vacancies ? makeVacancyIcon() : ICONS.property}
          >
            <Popup><PropertyPopup p={p} /></Popup>
          </Marker>
        ))}

        {/* Business markers */}
        {mapMode === 'businesses' && businesses.map(b => (
          <Marker
            key={`business-${b.id}`}
            position={[b.latitude, b.longitude]}
            icon={ICONS.business}
          >
            <Popup><BusinessPopup b={b} /></Popup>
          </Marker>
        ))}

        {/* Friend markers */}
        {mapMode === 'friends' && friends.map(f => (
          <Marker
            key={`friend-${f.id}`}
            position={[f.latitude, f.longitude]}
            icon={ICONS.friend}
          >
            <Popup><FriendPopup f={f} /></Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* ── Top controls — mirrors mobile topControls + modeRow ── */}
      <div className="absolute top-4 left-4 right-4 z-[1000] flex items-center gap-2">
        {/* Mode toggle pill — mirrors mobile modeRow */}
        <div className="flex-1 flex items-center bg-light-card dark:bg-dark-card rounded-xl p-1 shadow-medium border border-light-border dark:border-[#1e1e1e]">
          {modes.map(({ key, label, Icon, color }) => {
            const active = mapMode === key;
            return (
              <button
                key={key}
                onClick={() => setMapMode(key)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg transition-all text-sm font-semibold"
                style={active
                  ? { backgroundColor: color, color: '#fff' }
                  : { color: '#64748B' }
                }
              >
                <Icon size={16} />
                <span className="hidden sm:inline">{label}</span>
              </button>
            );
          })}
        </div>

        {/* Refresh button */}
        <button
          onClick={() => fetchData(mapMode)}
          disabled={loading}
          className="w-10 h-10 flex items-center justify-center rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-[#1e1e1e] shadow-medium hover:opacity-80 transition-opacity"
        >
          <RefreshCw size={16} className={`text-light-text dark:text-dark-text ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* ── Count badge — top-center ── */}
      {count > 0 && !loading && (
        <div className="absolute top-[72px] left-1/2 -translate-x-1/2 z-[1000]">
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-light-card/90 dark:bg-dark-card/90 backdrop-blur-sm border border-light-border dark:border-[#1e1e1e] shadow-soft">
            <MapPin size={12} className="text-primary" />
            <span className="text-xs font-semibold text-light-text dark:text-dark-text">
              {count} {mapMode === 'properties' ? 'properties' : mapMode === 'businesses' ? 'businesses' : 'friends'} on map
            </span>
          </div>
        </div>
      )}

      {/* ── Loading indicator ── */}
      {loading && (
        <div className="absolute top-[72px] left-1/2 -translate-x-1/2 z-[1000]">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-light-card/90 dark:bg-dark-card/90 backdrop-blur-sm border border-light-border dark:border-[#1e1e1e] shadow-soft">
            <Loader2 size={13} className="animate-spin text-primary" />
            <span className="text-xs font-semibold text-light-text dark:text-dark-text">Loading…</span>
          </div>
        </div>
      )}

      {/* ── Locate-me button — mirrors mobile locationBtn ── */}
      <button
        onClick={() => {
          setFlyTarget(null); // reset so FlyTo re-triggers
          setTimeout(() => setFlyTarget(userLocation || DEFAULT_ORIGIN), 10);
          if (!userLocation) getLocation();
        }}
        disabled={geoLoading}
        className="absolute right-4 bottom-24 z-[1000] w-12 h-12 rounded-full bg-white dark:bg-dark-card shadow-medium border border-light-border dark:border-[#1e1e1e] flex items-center justify-center hover:opacity-90 transition-opacity"
        title="Go to my location"
      >
        {geoLoading
          ? <Loader2 size={20} className="animate-spin text-primary" />
          : <Locate size={20} className="text-primary" />
        }
      </button>

      {/* ── Legend — bottom-left ── */}
      <div className="absolute bottom-24 left-4 z-[1000]">
        <div className="bg-light-card/90 dark:bg-dark-card/90 backdrop-blur-sm rounded-xl border border-light-border dark:border-[#1e1e1e] px-3 py-2 shadow-soft">
          <p className="text-[10px] font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-1.5">Legend</p>
          {[
            { color: '#4F46E5', label: 'Properties' },
            { color: '#10B981', label: 'Businesses' },
            { color: '#8e44ad', label: 'Friends' },
            { color: '#22c55e', label: 'Has vacancy' },
          ].map(({ color, label }) => (
            <div key={label} className="flex items-center gap-2 mb-1 last:mb-0">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
              <span className="text-[11px] text-light-subtle dark:text-dark-subtle">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Leaflet popup styling */}
      <style>{`
        .leaflet-popup-content-wrapper {
          border-radius: 12px !important;
          padding: 0 !important;
          box-shadow: 0 8px 24px rgba(0,0,0,0.12) !important;
          border: 1px solid #E2E8F0;
        }
        .leaflet-popup-content { margin: 8px 10px !important; }
        .leaflet-popup-tip-container { display: none; }
        .leaflet-control-attribution { font-size: 9px !important; }
        .leaflet-control-zoom { border: none !important; }
        .leaflet-control-zoom a {
          background: white !important;
          border: 1px solid #E2E8F0 !important;
          border-radius: 8px !important;
          margin-bottom: 4px !important;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1) !important;
        }
      `}</style>
    </div>
  );
};

export default MapPage;
